import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Mail, LogOut, Save, Archive, BookOpen, Heart, Calendar,
  Clock, Image, Video, Mic, Sparkles, RefreshCw, ChevronDown, ChevronUp,
  FileText, Quote, Flame, Book, Feather, Star, Film, Music, Trash2, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { pageTransition, fadeUp } from "@/lib/animations";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  vault: {
    photo: { icon: Image, color: "text-sky-400", bg: "bg-sky-400/10", border: "border-sky-400/20", label: "Photo" },
    video: { icon: Video, color: "text-violet-400", bg: "bg-violet-400/10", border: "border-violet-400/20", label: "Video" },
    voice_note: { icon: Mic, color: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/20", label: "Voice Note" },
    document: { icon: FileText, color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20", label: "Document" },
  },
  journal: {
    journal: { icon: BookOpen, color: "text-rose-400", bg: "bg-rose-400/10", border: "border-rose-400/20", label: "Journal" },
    prompt_response: { icon: Quote, color: "text-[#00FF88]", bg: "bg-[#00FF88]/10", border: "border-[#00FF88]/20", label: "Prompt Response" },
  },
  prompt: {
    reflection: { icon: Sparkles, color: "text-[#00FF88]", bg: "bg-[#00FF88]/10", border: "border-[#00FF88]/20", label: "Daily Reflection" },
  },
};

function getConfig(item) {
  return typeConfig[item.type]?.[item.subtype] || {
    icon: Clock, color: "text-slate-400", bg: "bg-slate-400/10", border: "border-slate-400/20", label: "Memory"
  };
}

function formatMonthKey(key) {
  if (!key || key === "unknown") return "Timeless";
  try {
    const [year, month] = key.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  } catch {
    return key;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
}

function TimelineItem({ item, index, isLast }) {
  const config = getConfig(item);
  const Icon = config.icon;
  const isImage = item.type === "vault" && item.content_type?.startsWith("image");

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex gap-4 md:gap-6 group"
    >
      <div className="flex flex-col items-center shrink-0 w-10">
        <div className={`w-10 h-10 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center z-10 group-hover:scale-110 transition-transform duration-300`}>
          <Icon className={`w-4 h-4 ${config.color}`} strokeWidth={1.5} />
        </div>
        {!isLast && (
          <div className="w-px flex-1 bg-gradient-to-b from-white/10 to-transparent min-h-[40px]" />
        )}
      </div>

      <div className="flex-1 pb-8 min-w-0">
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[20px] p-5 hover:border-white/[0.12] transition-colors duration-300 overflow-hidden">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-[10px] font-semibold tracking-wider uppercase ${config.color}`}>
              {config.label}
            </span>
            <span className="text-[10px] text-slate-600">{formatDate(item.date)}</span>
          </div>

          <h4 className="text-sm font-medium text-white mb-1 truncate" style={{ fontFamily: "Manrope" }}>
            {item.title}
          </h4>

          {item.description && (
            <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{item.description}</p>
          )}

          {isImage && item.file_id && (
            <div className="mt-3 rounded-xl overflow-hidden max-h-48">
              <img
                src={`${BACKEND_URL}/api/vault/files/${item.file_id}`}
                alt={item.title}
                className="w-full h-full object-cover rounded-xl"
                loading="lazy"
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function MonthChapter({ chapter, items, startIndex }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 mb-6 group cursor-pointer w-full text-left"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#00FF88]/10 border border-[#00FF88]/20 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-[#00FF88]">{chapter.number}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Manrope" }}>
              {formatMonthKey(chapter.month_key)}
            </h3>
            {chapter.narrative && (
              <p className="text-xs text-slate-400 mt-0.5 line-clamp-1 italic">{chapter.narrative}</p>
            )}
          </div>
          <span className="text-xs text-slate-600 shrink-0">{chapter.entry_count} memories</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-600" /> : <ChevronDown className="w-4 h-4 text-slate-600" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {items.map((item, i) => (
              <TimelineItem key={item.id || `${chapter.month_key}-${i}`} item={item} index={startIndex + i} isLast={i === items.length - 1} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "" });
  const [saving, setSaving] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("story");
  
  // My Log state
  const [logs, setLogs] = useState([]);
  const [logTotals, setLogTotals] = useState({ book: 0, movie: 0, concert: 0 });
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState(null); // null = all
  const [deletingLog, setDeletingLog] = useState(null);

  useEffect(() => {
    loadProfile();
    loadTimeline();
    loadLogs();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get("/profile");
      setProfile(res.data);
      setForm({ name: res.data.name || "", bio: res.data.bio || "" });
    } catch {
      toast.error("Failed to load profile");
    }
  };

  const loadTimeline = async () => {
    setLoadingTimeline(true);
    try {
      const res = await api.get("/timeline");
      setTimeline(res.data);
    } catch (err) {
      console.error("Timeline load error:", err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const loadLogs = async (filterType = null) => {
    setLoadingLogs(true);
    try {
      const url = filterType ? `/quick-logs?log_type=${filterType}` : "/quick-logs";
      const res = await api.get(url);
      setLogs(res.data.logs || []);
      setLogTotals(res.data.totals || { book: 0, movie: 0, concert: 0 });
    } catch (err) {
      console.error("Logs load error:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    setDeletingLog(logId);
    try {
      await api.delete(`/quick-log/${logId}`);
      setLogs(logs.filter(l => l.log_id !== logId));
      // Update totals
      const deleted = logs.find(l => l.log_id === logId);
      if (deleted) {
        setLogTotals(prev => ({
          ...prev,
          [deleted.log_type]: Math.max(0, (prev[deleted.log_type] || 0) - 1)
        }));
      }
      toast.success("Entry deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeletingLog(null);
    }
  };

  const handleFilterChange = (type) => {
    setLogFilter(type);
    loadLogs(type);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refreshUser();
      setEditing(false);
      toast.success("Profile updated");
      loadProfile();
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post("/timeline/regenerate");
      setTimeline(res.data);
      toast.success("Your story has been refreshed!");
    } catch {
      toast.error("Failed to regenerate story");
    } finally {
      setRegenerating(false);
    }
  };

  const stats = profile?.stats || {};
  const book = timeline?.book || {};

  // Group items by month
  const groupedByMonth = {};
  if (timeline?.items) {
    for (const item of timeline.items) {
      const dateStr = item.date || "";
      let monthKey = "unknown";
      try {
        if (dateStr.length >= 7) monthKey = dateStr.slice(0, 7);
      } catch { /* ignore */ }
      if (!groupedByMonth[monthKey]) groupedByMonth[monthKey] = [];
      groupedByMonth[monthKey].push(item);
    }
  }

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-4xl mx-auto pb-24" data-testid="profile-page">
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Profile</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {profile?.name || "Your Profile"}
        </h1>
      </div>

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[20px] p-6 mb-6"
      >
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-2xl bg-[#00FF88]/10 flex items-center justify-center shrink-0">
            {profile?.picture || profile?.avatar_url ? (
              <img src={profile.picture || profile.avatar_url} alt={profile.name} className="w-16 h-16 rounded-2xl object-cover" />
            ) : (
              <User className="w-7 h-7 text-[#00FF88]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="space-y-3">
                <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white rounded-xl h-10" />
                <textarea placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-3 h-20 resize-none outline-none text-sm" />
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={saving} className="bg-white text-black hover:bg-slate-200 rounded-full px-5 text-sm">
                    <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving..." : "Save"}
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(false)} className="border-white/10 text-slate-400 rounded-full text-sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{profile?.name || "..."}</h2>
                <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {profile?.email}</p>
                {profile?.bio && <p className="text-sm text-slate-300 mt-2">{profile.bio}</p>}
                <div className="flex items-center gap-3 mt-3">
                  <Button onClick={() => setEditing(true)} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-full px-4 text-xs">Edit Profile</Button>
                  <Button onClick={handleLogout} className="bg-white/5 border border-white/10 text-red-400 hover:bg-red-500/10 rounded-full px-4 text-xs">
                    <LogOut className="w-3 h-3 mr-1.5" /> Sign Out
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-8">
        {[
          { label: "Vault", count: stats.vault_entries || 0, icon: Archive },
          { label: "Journal", count: stats.journal_entries || 0, icon: BookOpen },
          { label: "Legacy", count: stats.legacy_messages || 0, icon: Heart },
          { label: "Chapters", count: book.total_chapters || 0, icon: Book },
        ].map((s, i) => (
          <div key={i} className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[16px] p-4 text-center">
            <s.icon className="w-4 h-4 text-slate-500 mx-auto mb-1.5" strokeWidth={1.5} />
            <p className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{s.count}</p>
            <p className="text-[10px] text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white/[0.02] border border-white/[0.06] rounded-xl p-1.5 w-fit">
        <button
          onClick={() => setActiveTab("story")}
          data-testid="tab-story"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === "story" ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "text-slate-500 hover:text-white"
          }`}
        >
          <Book className="w-3.5 h-3.5" /> Your Book
        </button>
        <button
          onClick={() => setActiveTab("timeline")}
          data-testid="tab-timeline"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === "timeline" ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "text-slate-500 hover:text-white"
          }`}
        >
          <Clock className="w-3.5 h-3.5" /> Timeline
        </button>
        <button
          onClick={() => setActiveTab("mylog")}
          data-testid="tab-mylog"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === "mylog" ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "text-slate-500 hover:text-white"
          }`}
        >
          <Star className="w-3.5 h-3.5" /> My Log
        </button>
      </div>

      {/* Your Book Tab */}
      {activeTab === "story" && (
        <div>
          {loadingTimeline ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-12 h-12 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-sm text-slate-500">Crafting your story...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Narrative with AI Image */}
              {timeline?.narrative && (
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                  <div className="relative rounded-2xl overflow-hidden">
                    {/* Background Image */}
                    {timeline.narrative_image && (
                      <div className="absolute inset-0">
                        <img
                          src={`${BACKEND_URL}/api/vault/files/${timeline.narrative_image}`}
                          alt="Your narrative"
                          className="w-full h-full object-cover opacity-30"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
                      </div>
                    )}
                    
                    <div className={`relative p-8 md:p-10 border border-[#00FF88]/15 rounded-2xl ${!timeline.narrative_image ? 'bg-gradient-to-br from-[#00FF88]/5 via-[#0A0A0A] to-[#0A0A0A]' : ''}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <Flame className="w-4 h-4 text-[#00FF88]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#00FF88] font-medium">Your Narrative</span>
                      </div>
                      <p className="text-base md:text-lg text-slate-200 leading-relaxed italic" style={{ fontFamily: "Manrope" }}>
                        "{timeline.narrative}"
                      </p>
                      
                      {/* Book Progress */}
                      <div className="mt-6 pt-4 border-t border-white/[0.06]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-500">Book Progress</span>
                          <span className="text-xs text-[#00FF88] font-medium">{book.completion_percentage || 0}% complete</span>
                        </div>
                        <Progress value={book.completion_percentage || 0} className="h-2 bg-white/5" />
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-slate-500">{book.words_written || 0} words written</span>
                          <span className="text-xs text-slate-500">{book.total_chapters || 0} chapters</span>
                        </div>
                      </div>

                      {/* Tip */}
                      <div className="mt-4 flex items-start gap-2 bg-[#00FF88]/5 border border-[#00FF88]/10 rounded-xl p-3">
                        <Feather className="w-4 h-4 text-[#00FF88] shrink-0 mt-0.5" />
                        <p className="text-xs text-slate-400">
                          <span className="text-[#00FF88] font-medium">Keep writing your story!</span> Complete daily prompts, add journal entries, and upload memories to grow your book. Every entry adds a new page to your life story.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Chapters List */}
              {book.chapters && book.chapters.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Book className="w-4 h-4" /> Your Chapters
                  </h3>
                  <div className="space-y-3">
                    {book.chapters.map((chapter) => (
                      <motion.div
                        key={chapter.month_key}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[16px] p-4 hover:border-white/[0.12] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#00FF88]/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-[#00FF88]">{chapter.number}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white">{formatMonthKey(chapter.month_key)}</p>
                            <p className="text-xs text-slate-400 mt-0.5 line-clamp-1 italic">{chapter.narrative}</p>
                          </div>
                          <span className="text-xs text-slate-500">{chapter.entry_count} entries</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Regenerate Button */}
              <div className="flex justify-center">
                <Button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-full px-6 font-medium"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? "animate-spin" : ""}`} />
                  {regenerating ? "Regenerating..." : "Refresh Story"}
                </Button>
              </div>

              {/* Empty State */}
              {(!timeline?.items || timeline.items.length === 0) && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-[#00FF88]/10 flex items-center justify-center mx-auto mb-6">
                    <Book className="w-8 h-8 text-[#00FF88]" />
                  </div>
                  <p className="text-lg text-white mb-2" style={{ fontFamily: "Manrope" }}>Your book starts here</p>
                  <p className="text-sm text-slate-500 max-w-md mx-auto">
                    Upload photos, write journal entries, and respond to daily prompts to begin writing your life story.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab === "timeline" && (
        <div>
          {loadingTimeline ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : book.chapters && book.chapters.length > 0 ? (
            <div>
              {book.chapters.map((chapter, mIdx) => {
                const items = groupedByMonth[chapter.month_key] || [];
                const prevCount = book.chapters.slice(0, mIdx).reduce((acc, c) => acc + (groupedByMonth[c.month_key]?.length || 0), 0);
                return (
                  <MonthChapter
                    key={chapter.month_key}
                    chapter={chapter}
                    items={items}
                    startIndex={prevCount}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Clock className="w-10 h-10 text-slate-600 mx-auto mb-4" />
              <p className="text-sm text-slate-500">No memories yet. Start adding entries to see your timeline!</p>
            </div>
          )}
        </div>
      )}

      {/* My Log Tab */}
      {activeTab === "mylog" && (
        <div data-testid="my-log-section">
          {/* Log Type Filters */}
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { type: null, label: "All", icon: Star, count: logTotals.book + logTotals.movie + logTotals.concert },
              { type: "book", label: "Books", icon: Book, count: logTotals.book, emoji: "📚", color: "rose" },
              { type: "movie", label: "Movies", icon: Film, count: logTotals.movie, emoji: "🎬", color: "cyan" },
              { type: "concert", label: "Concerts", icon: Music, count: logTotals.concert, emoji: "🎵", color: "pink" },
            ].map((f) => (
              <button
                key={f.type || "all"}
                onClick={() => handleFilterChange(f.type)}
                data-testid={`filter-${f.type || "all"}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  logFilter === f.type
                    ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/30"
                    : "bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                }`}
              >
                {f.emoji ? <span>{f.emoji}</span> : <f.icon className="w-4 h-4" />}
                {f.label}
                <span className={`text-xs ${logFilter === f.type ? "text-[#00FF88]/70" : "text-slate-600"}`}>
                  ({f.count})
                </span>
              </button>
            ))}
          </div>

          {/* Log List */}
          {loadingLogs ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin" />
            </div>
          ) : logs.length > 0 ? (
            <div className="space-y-3">
              {logs.map((log, idx) => {
                const typeConfig = {
                  book: { icon: Book, emoji: "📚", color: "rose", label: "Book" },
                  movie: { icon: Film, emoji: "🎬", color: "cyan", label: "Movie" },
                  concert: { icon: Music, emoji: "🎵", color: "pink", label: "Concert" },
                };
                const config = typeConfig[log.log_type] || typeConfig.book;

                return (
                  <motion.div
                    key={log.log_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    data-testid={`log-item-${log.log_id}`}
                    className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[20px] p-5 hover:border-white/[0.12] transition-all group"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-xl bg-${config.color}-500/10 border border-${config.color}-500/20 flex items-center justify-center shrink-0`}>
                        <span className="text-xl">{config.emoji}</span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-semibold tracking-wider uppercase text-${config.color}-400`}>
                            {config.label}
                          </span>
                          <span className="text-[10px] text-slate-600">
                            {new Date(log.date || log.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                        <h4 className="text-base font-semibold text-white" style={{ fontFamily: "Manrope" }}>
                          {log.title}
                        </h4>
                        {log.details && (
                          <p className="text-sm text-slate-400 mt-1 line-clamp-2">{log.details}</p>
                        )}

                        {/* Rating */}
                        {log.rating > 0 && (
                          <div className="flex items-center gap-0.5 mt-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= log.rating ? "text-[#00FF88] fill-[#00FF88]" : "text-slate-700"
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteLog(log.log_id)}
                        disabled={deletingLog === log.log_id}
                        className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-red-500/10 text-slate-600 hover:text-red-400 transition-all"
                      >
                        {deletingLog === log.log_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
                <Star className="w-8 h-8 text-slate-600" />
              </div>
              <p className="text-lg text-white mb-2" style={{ fontFamily: "Manrope" }}>
                {logFilter ? `No ${logFilter}s logged yet` : "No logs yet"}
              </p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                Use the Quick Actions on your Dashboard to log books you've read, movies you've watched, and concerts you've attended.
              </p>
            </div>
          )}

          {/* Stats Summary */}
          {(logTotals.book > 0 || logTotals.movie > 0 || logTotals.concert > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 bg-gradient-to-br from-[#00FF88]/5 via-[#0A0A0A] to-[#0A0A0A] border border-[#00FF88]/15 rounded-2xl p-6"
            >
              <h4 className="text-xs tracking-[0.2em] uppercase text-[#00FF88] font-medium mb-4">Your Journey</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
                    {logTotals.book}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">📚 Books Read</p>
                </div>
                <div className="text-center border-x border-white/[0.06]">
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
                    {logTotals.movie}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">🎬 Movies Watched</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
                    {logTotals.concert}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">🎵 Concerts Attended</p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </motion.div>
  );
}
